import React, { useState } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import Button from "../../../componentLibrary/Button";
import Tooltip from "../../../componentLibrary/Tooltip";
import Accordion from "../../../componentLibrary/Accordion";
import KeyTakeawaysList from "../../../componentLibrary/KeyTakeawaysList";
import ResponseModal from "../../../componentLibrary/ResponseModal";

type RecapValue = {
  label: string;
  value: string | string[];
  displayAs?: string;
};

type RecapEntry = {
  [key: string]: RecapValue | undefined;
};

const KeyTakeaways = ({
  keyTakeaways,
  lessonTitle,
}: {
  keyTakeaways: { takeaway: string; content: RecapEntry }[];
  lessonTitle: string;
}) => {
  const [selectedTakeaway, setSelectedTakeaway] = useState<{
    takeawayName: string;
    takeawayContent: RecapEntry;
  } | null>(null);

  const handleOpenTakeawayModal = (takeaway: {
    takeaway: string;
    content: RecapEntry;
  }) => {
    setSelectedTakeaway({
      takeawayName: takeaway.takeaway,
      takeawayContent: takeaway.content,
    });
  };

  const handleCloseTakeawayModal = () => {
    setSelectedTakeaway(null);
  };

  return (
    <div className="mt-4">
      <h3 className="font-medium mb-2">
        {keyTakeaways.length > 0
          ? `What I've learned about ${lessonTitle}:`
          : `We've completed ${lessonTitle}`}
      </h3>
      {keyTakeaways.length > 0 && (
        <Accordion
          title={
            <div className="flex items-center gap-2">
              <div className="text-md font-bold">Key Takeaways</div>
              <Tooltip
                icon={<InformationCircleIcon className="w-4 h-4" />}
                text="Key Takeaways are the most important concepts and ideas from the lesson."
                position="right"
                maxWidth={175}
              />
            </div>
          }
          defaultOpen={true}
          headerClassName="p-2 w-full"
        >
          <KeyTakeawaysList
            takeaways={keyTakeaways.map((takeaway, index) => ({
              id: `takeaway-${index}`,
              title: takeaway.takeaway,
              hasResponses: Object.keys(takeaway.content).length > 0,
              onActionClick: () => handleOpenTakeawayModal(takeaway),
            }))}
            actionLabel="View my Responses"
            actionButtonClassName="max-w-24"
            showTutorial={true}
          />
        </Accordion>
      )}

      {selectedTakeaway && (
        <ResponseModal
          isOpen={!!selectedTakeaway}
          onClose={handleCloseTakeawayModal}
          title={selectedTakeaway.takeawayName}
          content={selectedTakeaway.takeawayContent}
          footer={
            <div className="flex justify-center">
              <Button
                size="sm"
                variant="alternative"
                onClick={handleCloseTakeawayModal}
              >
                Close
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default KeyTakeaways;
